import React, { useState, useEffect } from "react";
import logo from "../../../assets/images/others/host-side-images/logo.png";
import { useForm, Controller } from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import { siteRoutes } from "../../../utils/helpers/enums/routes";
import useAuth from "../useHooks";
import { errorToaster } from "../../../utils/helpers/common/alert-service";

type FormValues = {
  email: string;
  otp: string[];
};

const OtpVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { veifyOtpForSignup, verifyOtpForget, resendOTP } = useAuth();
  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: { otp: ["", "", "", ""] },
  });

  // Store email and type in state to persist across navigation
  const [email, setEmail] = useState<string>(
    location.state?.email || "No email provided"
  );
  const [type, setType] = useState<string>(
    location.state?.type || "signup"
  );

  // Update state when location state changes (e.g., initial navigation)
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.type) {
      setType(location.state.type);
    }
  }, [location.state]);

  const onSubmit = async (data: FormValues) => {
    const otpCode = data.otp.join("");
    if (type === "signup") {
      await veifyOtpForSignup({ email, otp: otpCode });
    } else if (type === "reset") {
      const isVerified = await verifyOtpForget({ email, otp: otpCode });
      if (!isVerified) {
        errorToaster("Invalid OTP.");
      }
      if(isVerified) {  

        navigate(siteRoutes.resetPassword, {
          state: { email: email, otp: otpCode },
        });
      }
    }
  };

  const [seconds, setSeconds] = useState(60);

  useEffect(() => {
    if (seconds > 0) {
      const interval = setInterval(() => {
        setSeconds((prev) => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [seconds]);

  // Handler for resending the OTP.
  const handleResend = async () => {
    try {
      // Pass the current type to preserve it during resend
      await resendOTP({ email }, type);
      // Reset the timer to 60 seconds after resending OTP.
      setSeconds(60);
    } catch (error) {
      console.error("Error resending OTP:", error);
    }
  };

  return (
    <div className="w-full h-screen flex flex-col p-4 sm:p-6 lg:px-8 xl:px-10 lg:py-6">
      <div className="flex justify-center my-6">
        <img src={logo} alt="logo" className="h-20" />
      </div>
      <div className="flex-1 flex justify-center">
        <div className="w-full">
          <div className="w-full mb-6">
            <h1 className="text-3xl font-medium text-blackie">
              {type === "signup"
                ? "Verify Account"
                : "Reset Password Verification"}
            </h1>
            <p className="text-lg text-labelTextColor mt-2">
              {type === "signup"
                ? "Verify your account by entering the verification code we sent to "
                : "Enter the OTP sent to your email to reset your password "}
              <span className="font-semibold text-primaryBlueText">
                {email}
              </span>
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
            {/* OTP Label */}
            <label className="block text-bas font-medium text-label mb-2">
              Enter the OTP
            </label>
            <div className="flex space-x-4">
              {[0, 1, 2, 3].map((index) => (
                <Controller
                  key={index}
                  name={`otp.${index}`}
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <input
                      {...field}
                      type="text"
                      inputMode="numeric"
                      value={field.value || ""}
                      className={`w-12 h-12 text-center text-xl border rounded-lg ${
                        errors.otp?.[index]
                          ? "border-red-500"
                          : "border-borderColor"
                      } focus:outline-none`}
                      maxLength={1}
                      id={`otp-${index}`}
                      onFocus={(e) => {
                        // Select all text when focusing on a field with a value
                        e.target.select();
                      }}
                      onChange={(e) => {
                        // Get the last character entered (handles paste and typing)
                        const value = e.target.value.slice(-1);
                        // Only allow numeric digits (0-9)
                        if (value && /^[0-9]$/.test(value)) {
                          field.onChange(value);
                          // Move to next field if value is entered
                          if (index < 3) {
                            const nextField = document.getElementById(
                              `otp-${index + 1}`
                            ) as HTMLInputElement;
                            nextField?.focus();
                            nextField?.select();
                          }
                        } else if (e.target.value === "") {
                          // Allow clearing the field
                          field.onChange("");
                        }
                      }}
                      onKeyDown={(e) => {
                        // Handle backspace to move to previous field
                        if (e.key === "Backspace" && !field.value) {
                          if (index > 0) {
                            const prevField = document.getElementById(
                              `otp-${index - 1}`
                            ) as HTMLInputElement;
                            prevField?.focus();
                            prevField?.select();
                          }
                        }
                        // Handle arrow keys for navigation
                        if (e.key === "ArrowLeft" && index > 0) {
                          e.preventDefault();
                          const prevField = document.getElementById(
                            `otp-${index - 1}`
                          ) as HTMLInputElement;
                          prevField?.focus();
                          prevField?.select();
                        }
                        if (e.key === "ArrowRight" && index < 3) {
                          e.preventDefault();
                          const nextField = document.getElementById(
                            `otp-${index + 1}`
                          ) as HTMLInputElement;
                          nextField?.focus();
                          nextField?.select();
                        }
                      }}
                    />
                  )}
                />
              ))}
            </div>
            {errors.otp && (
              <p className="text-red-500 text-sm mt-1">
                Please fill all fields.
              </p>
            )}
            {/* <div className="mt-2 text-sm text-yellowText">
              {seconds > 0
                ? `00:${seconds < 10 ? `0${seconds}` : seconds}`
                : "00:00"}
            </div> */}
            <div className="flex justify-start flex-col gap-2 mt-2">
              <button
                type="submit"
                className="lg:h-[64px] p-3 mt-2 text-xl bg-primaryBlueBg rounded-full text-white whitespace-nowrap w-full sm:w-2/3 md:w-1/2"
              >
                {type === "signup" ? "Verify" : "Reset Password"}
              </button>
              <div className="flex gap-1 text-center justify-center whitespace-nowrap w-full sm:w-2/3 md:w-1/2">
                Didn't get the OTP?
                <button
                  type="button"
                  onClick={handleResend}
                  className="text-sm text-center text-yellowText "
                  disabled={seconds > 0}
                >
                  {seconds > 0 ? `Resend in ${seconds} sec` : "Resend Code"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
